<script setup lang="ts">
import type { FrontendUserAvatarIdentityStatus, FrontendUserAvatarIdentityVerificationItem } from '@/api/modules/frontendUser'
import type { ProTableColumn } from '@/components/FaProTable/types'
import { FaAvatar, FaButton } from '@fantastic-admin/components'
import { computed, h, shallowRef } from 'vue'
import AppDetailSection from '@/components/AppDisplay/DetailSection.vue'
import AppStatusTag from '@/components/AppDisplay/StatusTag.vue'
import FaProTable from '@/components/FaProTable/index.vue'
import { formatDateTime } from '../../utils/userDisplay'
import AvatarIdentityInvalidateDialog from './AvatarIdentityInvalidateDialog.vue'

defineOptions({ name: 'AvatarIdentityRecords' })

const props = defineProps<{
  userId: number
  records: FrontendUserAvatarIdentityVerificationItem[]
  canInvalidate: boolean
}>()
const emit = defineEmits<{ updated: [] }>()
const detailVisible = shallowRef(false)
const invalidateVisible = shallowRef(false)
const currentRecord = shallowRef<FrontendUserAvatarIdentityVerificationItem | null>(null)

const statusMap: Record<FrontendUserAvatarIdentityStatus, { label: string, tone: 'default' | 'success' | 'warning' | 'danger' }> = {
  processing: { label: '认证中', tone: 'warning' },
  passed: { label: '已通过', tone: 'success' },
  failed: { label: '未通过', tone: 'danger' },
  error: { label: '服务异常', tone: 'danger' },
  invalidated: { label: '已撤销', tone: 'default' },
}

function statusMeta(status: FrontendUserAvatarIdentityStatus) {
  return statusMap[status] ?? { label: status || '未知', tone: 'default' as const }
}

function openDetail(record: FrontendUserAvatarIdentityVerificationItem) {
  currentRecord.value = record
  detailVisible.value = true
}

function openInvalidate(record: FrontendUserAvatarIdentityVerificationItem) {
  currentRecord.value = record
  invalidateVisible.value = true
}

async function handleInvalidated() {
  invalidateVisible.value = false
  emit('updated')
}

const detailItems = computed(() => {
  const record = currentRecord.value
  if (!record) {
    return []
  }
  return [
    { label: '记录 ID', value: record.id },
    { label: '附件 ID', value: record.attachment_id },
    { label: '认证状态', value: statusMeta(record.status).label },
    { label: '认证服务', value: `${record.provider} / ${record.product_code}` },
    { label: '比对分数', value: record.verify_score || '-' },
    { label: '请求编号', value: record.provider_request_id || '-', copyable: Boolean(record.provider_request_id) },
    { label: '附件摘要', value: record.attachment_hash || '-', copyable: Boolean(record.attachment_hash) },
    { label: '发起时间', value: formatDateTime(record.created_at) },
    { label: '通过时间', value: formatDateTime(record.verified_at) },
    { label: '撤销时间', value: formatDateTime(record.invalidated_at) },
    { label: '失败编码', value: record.failure_code || '-', span: 2 },
    { label: '失败说明', value: record.failure_message || '-', span: 2 },
    { label: '撤销原因', value: record.invalidation_reason || '-', span: 2 },
  ]
})

const columns: ProTableColumn<FrontendUserAvatarIdentityVerificationItem>[] = [
  {
    prop: 'attachment_url',
    label: '认证头像',
    width: 96,
    render: ({ row }) => h(FaAvatar, { src: row.attachment_url || '', fallback: '头像', size: '42px' }),
  },
  {
    prop: 'status',
    label: '状态',
    width: 110,
    render: ({ row }) => {
      const meta = statusMeta(row.status)
      return h(AppStatusTag, { label: meta.label, tone: meta.tone })
    },
  },
  { prop: 'product_code', label: '认证方式', width: 110, formatter: row => row.product_code || '-' },
  { prop: 'verify_score', label: '比对分数', width: 110, formatter: row => row.verify_score || '-' },
  { prop: 'verified_at', label: '通过时间', width: 170, formatter: row => formatDateTime(row.verified_at) },
  { prop: 'created_at', label: '发起时间', width: 170, formatter: row => formatDateTime(row.created_at) },
  {
    prop: 'actions',
    label: '操作',
    width: 180,
    fixed: 'right',
    render: ({ row }) => h('div', { class: 'flex gap-2' }, [
      h(FaButton, { variant: 'outline', size: 'sm', onClick: () => openDetail(row) }, () => '详情'),
      props.canInvalidate && row.status === 'passed'
        ? h(FaButton, { variant: 'destructive', size: 'sm', onClick: () => openInvalidate(row) }, () => '撤销')
        : null,
    ]),
  },
]
</script>

<template>
  <FaCard title="真人头像认证" content-class="pt-3">
    <div class="text-sm text-muted-foreground mb-3 flex gap-3 items-center justify-between">
      <span>共 {{ records.length }} 条认证记录；仅当前有效头像的通过记录会展示真人标识。</span>
    </div>
    <FaProTable
      :columns="columns"
      :data="records"
      :show-toolbar="false"
      :show-pagination="false"
      :bordered="false"
      row-key="id"
    />
  </FaCard>

  <FaModal v-model="detailVisible" title="真人头像认证详情" :footer="false" destroy-on-close class="sm:max-w-[760px]">
    <div v-if="currentRecord" class="space-y-5">
      <div class="flex gap-4 items-center">
        <FaAvatar :src="currentRecord.attachment_url || ''" fallback="头像" size="72px" />
        <div class="space-y-1">
          <AppStatusTag :label="statusMeta(currentRecord.status).label" :tone="statusMeta(currentRecord.status).tone" />
          <div class="text-sm text-muted-foreground">
            当前仅展示服务端保存的脱敏认证事实，不展示身份证或人脸特征数据。
          </div>
        </div>
      </div>
      <AppDetailSection
        title="认证事实"
        description="认证服务返回并由后台留存的脱敏结果"
        icon="i-ri:shield-user-line"
        :items="detailItems"
        :columns="2"
      />
    </div>
  </FaModal>

  <AvatarIdentityInvalidateDialog
    v-model="invalidateVisible"
    :user-id="userId"
    :record="currentRecord"
    @saved="handleInvalidated"
  />
</template>
